import type { Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';

interface UsePlannerInitializationOptions {
  /** Parent scroll container ref */
  parentRef: React.RefObject<HTMLDivElement>;
  /** Column virtualizer instance */
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  /** Whether data is still loading */
  isLoading: boolean;
  /** The date to scroll to initially */
  scrollDate: Date;
  /** Function to scroll to a specific date */
  scrollToDate: (date: Date, paddingLeftDays: number, smooth: boolean) => void;

  /** Ref to track if programmatic scroll is in progress (from usePlannerScroll) */
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
  /** LocalStorage instance for cleanup */
  storage: {
    get: (key: string) => string | null;
    set: (key: string, value: string) => void;
  };
}

interface UsePlannerInitializationResult {
  /** Whether initial scroll has completed (controls fade-in) */
  hasInitialScrolled: boolean;
}

/**
 * Handles planner initialization: storage cleanup and initial date scroll.
 *
 * This hook consolidates the initial scroll logic and provides state
 * that other effects can depend on. Site scrolling is handled separately
 * in the component after rowVirtualizer is created.
 *
 * Initialization sequence:
 * 1. Storage cleanup (once on mount)
 * 2. Wait for virtualizer readiness
 * 3. Initial scroll to date (non-smooth)
 * 4. Set hasInitialScrolled = true (triggers fade-in and site scroll)
 */
export function usePlannerInitialization({
  parentRef,
  columnVirtualizer,
  isLoading,
  scrollDate,
  scrollToDate,
  isProgrammaticScrollRef,
  storage,
}: UsePlannerInitializationOptions): UsePlannerInitializationResult {
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);
  const didInitialScrollRef = useRef(false);
  const storageCleanupRan = useRef(false);
  const scrollPendingRef = useRef(false);
  const lastScrollDateRef = useRef<Date | null>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // RAF IDs stored in refs so they can be cancelled in cleanup
  const subsequentRafIdRef = useRef<number>(0);
  const outerRafIdRef = useRef<number>(0);
  const innerRafIdRef = useRef<number>(0);
  const fallbackOuterRafIdRef = useRef<number>(0);
  const fallbackInnerRafIdRef = useRef<number>(0);

  // Storage cleanup effect - run once on mount
  useEffect(() => {
    if (!storageCleanupRan.current) {
      storage.set('draft.job.name', '');
      storage.set('draft.job.timestamp', '');
      storageCleanupRan.current = true;
    }
  }, [storage]);

  // Consolidated initial and subsequent scroll handling
  // Replaces the large effect that handled both initial and subsequent scrolls
  useEffect(() => {
    // Prevent multiple simultaneous scroll operations
    if (scrollPendingRef.current) {
      return;
    }

    // Subsequent updates: only react to scrollDate changes
    if (didInitialScrollRef.current) {
      const prevTime = lastScrollDateRef.current
        ? lastScrollDateRef.current.getTime()
        : undefined;
      const nextTime = scrollDate ? scrollDate.getTime() : undefined;

      if (prevTime !== nextTime) {
        scrollPendingRef.current = true;
        // Mark as programmatic scroll to prevent onVisibleDateChange during navigation
        isProgrammaticScrollRef.current = true;

        // Cancel any existing RAF before scheduling a new one
        if (subsequentRafIdRef.current) {
          cancelAnimationFrame(subsequentRafIdRef.current);
          subsequentRafIdRef.current = 0;
        }

        subsequentRafIdRef.current = requestAnimationFrame(() => {
          scrollToDate(scrollDate, 1, true);
          lastScrollDateRef.current = scrollDate;
          scrollPendingRef.current = false;

          // Clear programmatic flag after smooth scroll completes
          scrollTimeoutRef.current = setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 600);

          // Clear RAF id after successful execution
          subsequentRafIdRef.current = 0;
        });
      }
      return;
    }

    // Initial non-smooth scroll, wait for readiness
    if (isLoading) {
      return;
    }

    const isReady =
      Boolean(parentRef.current) && Boolean(columnVirtualizer.range);

    if (isReady) {
      scrollPendingRef.current = true;

      // Defer initial scroll to next frame to ensure layout is stable
      outerRafIdRef.current = requestAnimationFrame(() => {
        scrollToDate(scrollDate, 1, false);
        didInitialScrollRef.current = true;
        lastScrollDateRef.current = scrollDate;
        scrollPendingRef.current = false;

        // Mark initial scroll complete and allow fade-in
        innerRafIdRef.current = requestAnimationFrame(() => {
          setHasInitialScrolled(true);
          // Clear inner RAF id after successful execution
          innerRafIdRef.current = 0;
        });
        // Clear outer RAF id after successful execution
        outerRafIdRef.current = 0;
      });
      return;
    }

    // Fallback: retry with requestAnimationFrame once layout stabilizes
    fallbackOuterRafIdRef.current = requestAnimationFrame(() => {
      if (didInitialScrollRef.current) {
        // Clear outer RAF id on early return
        fallbackOuterRafIdRef.current = 0;
        return;
      }

      scrollToDate(scrollDate, 1, false);
      didInitialScrollRef.current = true;
      lastScrollDateRef.current = scrollDate;
      scrollPendingRef.current = false;

      fallbackInnerRafIdRef.current = requestAnimationFrame(() => {
        setHasInitialScrolled(true);
        // Clear inner RAF id after successful execution
        fallbackInnerRafIdRef.current = 0;
      });

      // Clear outer RAF id after successful execution
      fallbackOuterRafIdRef.current = 0;
    });

    // Cleanup function to cancel all pending RAF callbacks
    return () => {
      if (subsequentRafIdRef.current) {
        cancelAnimationFrame(subsequentRafIdRef.current);
        subsequentRafIdRef.current = 0;
      }
      if (outerRafIdRef.current) {
        cancelAnimationFrame(outerRafIdRef.current);
        outerRafIdRef.current = 0;
      }
      if (innerRafIdRef.current) {
        cancelAnimationFrame(innerRafIdRef.current);
        innerRafIdRef.current = 0;
      }
      if (fallbackOuterRafIdRef.current) {
        cancelAnimationFrame(fallbackOuterRafIdRef.current);
        fallbackOuterRafIdRef.current = 0;
      }
      if (fallbackInnerRafIdRef.current) {
        cancelAnimationFrame(fallbackInnerRafIdRef.current);
        fallbackInnerRafIdRef.current = 0;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      // Ensure pending flag is cleared if callbacks are cancelled before running
      scrollPendingRef.current = false;
    };
    // isProgrammaticScrollRef is a ref and doesn't need to be in the dependency array
    // columnVirtualizer.range is accessed but we only care about its existence (truthy/falsy),
    // not its specific values which change on every render. We use a stable boolean instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    scrollDate,
    scrollToDate,
    !!columnVirtualizer.range,
    parentRef,
  ]);

  return {
    hasInitialScrolled,
  };
}
